import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { IconButton } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { BellRing, RefreshCcw } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import { TaskCommentDialog } from './TaskCommentDialog';
import { useTaskReviewActions } from './use-task-review-actions';

const useStyles = makeStyles()(theme => ({
  button: {
    color: theme.appColors.iconsType,
    flexShrink: 0,
    margin: theme.spacing(-0.5, 0),
  },
  // A plain inline span would set the button on the text baseline, below the row's other icons.
  tooltipTarget: {
    display: 'inline-flex',
    flexShrink: 0,
  },
}));

interface ReviewerRowActionsProps {
  // Set when the viewer can't act on the task right now; shown as the tooltip.
  disabledReason: string | null;
  // The reviewer answered, so asking again starts the next round.
  hasAnswered: boolean;
  // The reviewer was asked and hasn't answered.
  isPending: boolean;
  name: string;
  permissionId: UUID;
  taskId: UUID;
}

/**
 * An assignee's actions on one reviewer: remind them while they review, or ask them (again) once
 * they have answered or were never asked.
 */
export const ReviewerRowActions = React.memo(function ReviewerRowActions({
  disabledReason,
  hasAnswered,
  isPending,
  name,
  permissionId,
  taskId,
}: ReviewerRowActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const actions = useTaskReviewActions(taskId);
  const action = getRowAction({ hasAnswered, isPending, name });

  return (
    <>
      <RowIconButton
        disabledReason={disabledReason}
        icon={action.icon}
        label={action.title}
        onClick={() => setIsOpen(true)}
      />
      <TaskCommentDialog
        label="Note"
        message={action.message}
        open={isOpen}
        placeholder={`Type a note for ${name}...`}
        primaryText={action.primaryText}
        title={action.title}
        onClose={() => setIsOpen(false)}
        onSubmit={note =>
          isPending
            ? actions.remindReviewers(note, permissionId)
            : actions.requestReview({ note, reviewerPermissionIds: [permissionId] })
        }
      />
    </>
  );
});

function getRowAction(options: { hasAnswered: boolean; isPending: boolean; name: string }): {
  icon: LucideIcon;
  message: string;
  primaryText: string;
  title: string;
} {
  const { hasAnswered, isPending, name } = options;
  if (isPending)
    return {
      icon: BellRing,
      message: `We'll email ${name} a reminder to review.`,
      primaryText: 'Send Reminder',
      title: `Remind ${name}`,
    };
  const label = hasAnswered ? 'Request Re-Review' : 'Request Review';
  return {
    icon: RefreshCcw,
    message: hasAnswered
      ? `${name} is asked to review again, which starts the next round.`
      : `${name} is asked to review.`,
    primaryText: label,
    title: `${label} from ${name}`,
  };
}

function RowIconButton({
  disabledReason,
  icon: Icon,
  label,
  onClick,
}: {
  disabledReason: string | null;
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <AlineTooltip arrow placement="top" title={disabledReason ?? label}>
      <span className={classes.tooltipTarget}>
        <IconButton
          aria-label={label}
          className={classes.button}
          disabled={Boolean(disabledReason)}
          size="small"
          onClick={onClick}
        >
          <Icon size={16} />
        </IconButton>
      </span>
    </AlineTooltip>
  );
}
